import React, { useState } from "react";
import { Copy, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";

export interface CopyPhotosLocationOption {
	id: string;
	name: string;
	photosCount: number;
}

interface CopyPhotosModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (sourceLocationId: string) => void | Promise<void>;
	locationOptions: CopyPhotosLocationOption[];
	destinationLocationName: string;
	isLoading: boolean;
}

export const CopyPhotosModal: React.FC<CopyPhotosModalProps> = ({
	isOpen,
	onClose,
	onConfirm,
	locationOptions,
	destinationLocationName,
	isLoading
}) => {
	const [selectedLocationId, setSelectedLocationId] = useState("");

	const selected = locationOptions.find(opt => opt.id === selectedLocationId);

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			setSelectedLocationId("");
			onClose();
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={handleOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<Copy className="h-5 w-5" />
						Copy photos from location
					</DialogTitle>
					<DialogDescription>
						Select a location to copy all uploaded photos from into "{destinationLocationName}".
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<label className="text-sm font-medium text-gray-900 block mb-2">
							Choose source location:
						</label>
						<Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
							<SelectTrigger className="w-full">
								<SelectValue placeholder="Select a location to copy from..." />
							</SelectTrigger>
							<SelectContent>
								{locationOptions.map(loc => (
									<SelectItem key={loc.id} value={loc.id}>
										<div className="flex items-center justify-between w-full">
											<span>{loc.name}</span>
											<span className="ml-2 text-xs text-gray-500">
												({loc.photosCount} photo{loc.photosCount !== 1 ? "s" : ""})
											</span>
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{selected && (
						<Alert>
							<RefreshCw className="h-4 w-4" />
							<AlertDescription>
								This will copy <strong>{selected.photosCount} photo{selected.photosCount !== 1 ? "s" : ""}</strong> from "{selected.name}" and add them to the current location. Existing photos will be preserved.
								<br /><br />
								<strong>Note:</strong> The page will refresh after copying to show the updated photos list.
							</AlertDescription>
						</Alert>
					)}

					{locationOptions.length === 0 && (
						<Alert>
							<AlertDescription>
								No other locations found in this organization to copy from.
							</AlertDescription>
						</Alert>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose} disabled={isLoading}>
						Cancel
					</Button>
					<Button onClick={() => selectedLocationId && onConfirm(selectedLocationId)} disabled={!selectedLocationId || isLoading}>
						{isLoading ? "Copying..." : "Copy photos"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
